import React, { useMemo, useRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "./PlatesClaimsReview.scss";
import { Link } from "react-router-dom";
import {
  resolvePlate,
  rejectPlateClaim,
  requestMoreInfo,
} from "../../../services/platesService";
import {
  getPlateCaption,
  getPlateDisplayImageUrl,
  getPlateLocationCountry,
  getPlateLocationState,
  getPlateNumber,
  getProfileUser,
  mapPlateStatusLabel,
} from "../../../utils/platesProfileHelpers";

function PlatesClaimsReview() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const plateData = location.state?.plateData;
  const approveModalRef = useRef(null);
  const rejectModalRef = useRef(null);
  const requestInfoModalRef = useRef(null);

  const plateEntry = plateData?.plateEntry;
  const plateId = location.state?.plateId || plateEntry?._id;

  // State for reject claim form
  const [rejectSubject, setRejectSubject] = useState("");
  const [rejectMessage, setRejectMessage] = useState("");

  // State for request more information form
  const [requestMessage, setRequestMessage] = useState("");

  // Rejection reasons
  const rejectReasons = [
    "Plate image unclear",
    "Document Mismatch",
    "Duplicate Claim",
    "Other",
  ];

  // Resolve/Approve plate mutation
  const resolvePlateMutation = useMutation({
    mutationFn: (id) => resolvePlate(id),
    onSuccess: (data) => {
      toast.success(data?.message || "Plate claim approved successfully", {
        position: "top-right",
        autoClose: 3000,
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plates-by-status"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to approve plate claim";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
      });
    },
  });

  const handleApproveClaim = () => {
    if (!plateId) {
      toast.error("Plate ID is missing");
      return;
    }

    resolvePlateMutation.mutate(plateId);
  };

  // Reject plate claim mutation
  const rejectPlateMutation = useMutation({
    mutationFn: ({ id, rejectData }) => rejectPlateClaim(id, rejectData),
    onSuccess: (data) => {
      toast.success(data?.message || "Plate claim rejected successfully", {
        position: "top-right",
        autoClose: 3000,
      });

      // Reset form
      setRejectSubject("");
      setRejectMessage("");

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plates-by-status"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reject plate claim";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
      });
    },
  });

  const handleRejectClaim = () => {
    if (!plateId) {
      toast.error("Plate ID is missing");
      return;
    }

    // Validate required fields
    if (!rejectSubject.trim()) {
      toast.error("Please select a rejection reason", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (!rejectMessage.trim()) {
      toast.error("Please enter a rejection message", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    rejectPlateMutation.mutate({
      id: plateId,
      rejectData: {
        subject: rejectSubject.trim(),
        message: rejectMessage.trim(),
      },
    });
  };

  const handleRejectSubjectChange = (subject) => {
    setRejectSubject(subject);
  };

  // Request more information mutation
  const requestMoreInfoMutation = useMutation({
    mutationFn: ({ id, requestData }) => requestMoreInfo(id, requestData),
    onSuccess: (data) => {
      toast.success(
        data?.message || "Request for more information sent successfully",
        {
          position: "top-right",
          autoClose: 3000,
        }
      );

      // Reset form
      setRequestMessage("");

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plates-by-status"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send request for more information";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
      });
    },
  });

  const handleRequestMoreInfo = () => {
    if (!plateId) {
      toast.error("Plate ID is missing");
      return;
    }

    // Validate required field
    if (!requestMessage.trim()) {
      toast.error("Please enter a message", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    requestMoreInfoMutation.mutate({
      id: plateId,
      requestData: {
        message: requestMessage.trim(),
      },
    });
  };

  // Reset forms when modals are closed
  useEffect(() => {
    const rejectModalElement = rejectModalRef.current;
    const requestInfoModalElement = requestInfoModalRef.current;

    const handleRejectModalHidden = () => {
      setRejectSubject("");
      setRejectMessage("");
    };

    const handleRequestInfoModalHidden = () => {
      setRequestMessage("");
    };

    if (rejectModalElement) {
      rejectModalElement.addEventListener(
        "hidden.bs.modal",
        handleRejectModalHidden
      );
    }

    if (requestInfoModalElement) {
      requestInfoModalElement.addEventListener(
        "hidden.bs.modal",
        handleRequestInfoModalHidden
      );
    }

    return () => {
      if (rejectModalElement) {
        rejectModalElement.removeEventListener(
          "hidden.bs.modal",
          handleRejectModalHidden
        );
      }
      if (requestInfoModalElement) {
        requestInfoModalElement.removeEventListener(
          "hidden.bs.modal",
          handleRequestInfoModalHidden
        );
      }
    };
  }, []);

  // Cleanup on unmount or when modal is closed manually
  useEffect(() => {
    const modalElement = approveModalRef.current;

    if (modalElement) {
      const handleHidden = () => {
        // Remove all backdrops if they still exist
        const backdrops = document.querySelectorAll(".modal-backdrop");
        backdrops.forEach((backdrop) => backdrop.remove());

        // Remove modal-open class from body
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";

        // Remove aria-hidden attribute
        document.body.removeAttribute("aria-hidden");

        // Remove focus from modal element
        if (modalElement.contains(document.activeElement)) {
          // Blur the active element
          if (
            document.activeElement &&
            typeof document.activeElement.blur === "function"
          ) {
            document.activeElement.blur();
          }
          // Try to focus a safe element
          const safeElement = document.querySelector(
            'header, .review-panel-header, button, a, [tabindex]:not([tabindex="-1"])'
          );
          if (safeElement && typeof safeElement.focus === "function") {
            setTimeout(() => safeElement.focus(), 0);
          }
        }

        // Remove focus from modal element
        modalElement.blur();
        modalElement.removeAttribute("tabindex");
        modalElement.setAttribute("aria-hidden", "true");

        // Remove any focus trap elements
        const focusTrapElements = document.querySelectorAll(
          "[data-bs-focus-trap]"
        );
        focusTrapElements.forEach((el) => el.remove());
      };

      modalElement.addEventListener("hidden.bs.modal", handleHidden);

      return () => {
        modalElement.removeEventListener("hidden.bs.modal", handleHidden);
      };
    }
  }, []);

  // Transform API data to match display structure
  const displayData = useMemo(() => {
    if (!plateData) {
      // If no data, redirect back or show empty state
      return null;
    }

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        const timeStr = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return `${dateStr} | ${timeStr}`;
      } catch (e) {
        return dateString;
      }
    };

    const user = getProfileUser(plateData);
    const userName =
      plateData.businessName ||
      plateData.profilename ||
      plateData.username ||
      user.name ||
      user.fullName ||
      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      user.email ||
      "N/A";

    const userEmail = plateData.businessEmail || user.email || "N/A";
    const userId = user._id || user.id || "N/A";

    // Map KYC status
    const kycStatusMap = {
      verified: "Verified",
      unverified: "Unverified",
      pending: "Pending",
    };
    const kycStatus =
      kycStatusMap[user.kycVerification?.toLowerCase()] ||
      user.kycVerification?.charAt(0).toUpperCase() +
        user.kycVerification?.slice(1) ||
      "Unverified";

    // Map profile visibility
    const profileType =
      user.profileVisibility?.charAt(0).toUpperCase() +
        user.profileVisibility?.slice(1) || "Public";

    const plateStatus = mapPlateStatusLabel(
      plateEntry?.status ?? plateData.status
    );

    const plateNumber = getPlateNumber(plateEntry, plateData);
    const locationState = getPlateLocationState(plateEntry, plateData) || "N/A"
    const locationCountry = getPlateLocationCountry(plateEntry, plateData) || "N/A"

    const platePhoto =
      getPlateDisplayImageUrl(plateEntry, plateData) ||
      process.env.PUBLIC_URL + "/images/plate.svg";

    const captionText = getPlateCaption(plateEntry) || plateData.caption;

    return {
      sections: [
        {
          title: "Plate Information",
          items: [
            {
              label: "Plate Number",
              value: plateNumber,
            },
            {
              label: "Claim Status",
              value: plateStatus,
              type: "status",
            },
            {
              label: "Submission Date",
              value: formatDate(plateData.createdAt),
            },
            {
              label: "State / Region",
              value: locationState,
            },
            {
              label: "Country",
              value: locationCountry,
            },
            {
              label: "Plate entry ID",
              value: plateEntry?._id || plateData._id || plateData.claimId || "N/A",
            },
          ],
        },
        {
          title: "Media",
          items: [
            {
              label: "Plate Photo",
              image: platePhoto,
              type: "image",
            },
          ],
        },
        ...(captionText
          ? [
              {
                title: "Additional Information",
                items: [{ label: "Caption", value: captionText }],
              },
            ]
          : []),
        {
          title: "User Details",
          items: [
            { label: "User Name", value: userName },
            { label: "User ID", value: userId },
            { label: "Email", value: userEmail },
            {
              label: "KYC Status",
              value: kycStatus,
              type: kycStatus === "Verified" ? "status-approved" : "status",
            },
            { label: "Profile Type", value: profileType },
          ],
        },
      ],
    };
  }, [plateData, plateEntry]);

  // Redirect if no data
  if (!plateData) {
    return (
      <React.Fragment>
        <header className="review-panel-header items-center gap-3 flex content-justify-between">
          <div className="left-side">
            <Link to="/plates-management/pending-plates-claims">
              <img src={process.env.PUBLIC_URL + "/images/back.svg"} alt="back" />
            </Link>
            <h4 className="title">Plate Claim Review</h4>
          </div>
        </header>
        <div className="platesClaimsReview">
          <div className="card">
            <p>No plate data available. Please select a plate from the list.</p>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <header className="review-panel-header items-center gap-3 flex content-justify-between">
        <div className="left-side">
          <Link to="/plates-management/pending-plates-claims">
            <img src={process.env.PUBLIC_URL + "/images/back.svg"} alt="back" />
          </Link>
          <h4 className="title">Plate Claim Review</h4>
        </div>

        <div className="right-side">
          <button
            className="approve"
            data-bs-toggle="modal"
            data-bs-target="#approveClaimModal"
          >
            Approve Claim
          </button>
          <button
            className="reject"
            data-bs-toggle="modal"
            data-bs-target="#rejectClaimModal"
          >
            Reject Claim
          </button>
          <button
            className="request"
            data-bs-toggle="modal"
            data-bs-target="#requestInfoModal"
          >
            Request More Information
          </button>
        </div>
      </header>

      <div className="platesClaimsReview">
        <div className="row">
          {displayData.sections.map((section, index) => {
            let colSize = "12";

            if (section.title === "Plate Information") {
              colSize = "8";
            } else if (section.title === "Media") {
              colSize = "4";
            }

            return (
              <div
                key={index}
                className={`col-lg-${
                  section.title === "Media" ? "12" : "12"
                } col-xl-${colSize} `}
              >
                <div className="card">
                  <h4>{section.title}</h4>

                  <div className="inner-content">
                    <div className="row">
                      {section.items.map((item, i) => (
                        <div
                          key={i}
                          className={`col-lg-${
                            section.title === "Media" ? "12" : "4"
                          } col-xl-${section.title === "Media" ? "12" : "4"} `}
                        >
                          <div className="content-wrapper">
                            <img src={process.env.PUBLIC_URL + "/images/user-icon.svg"} alt="icon" />

                            <div className="content">
                              <h5>{item.label}</h5>

                              {/* STATUS BADGE */}
                              {item.type === "status" && (
                                <p
                                  className={`status-badge ${
                                    item.value?.toLowerCase() || "pending"
                                  }`}
                                >
                                  <span className="dot"></span> {item.value}
                                </p>
                              )}

                              {item.type === "status-approved" && (
                                <p className="status-badge approved">
                                  <span className="dot"></span> {item.value}
                                </p>
                              )}

                              {/* IMAGE TYPE */}
                              {item.type === "image" && (
                                <div className="car-plate">
                                  <img src={item.image} alt="plate" />
                                </div>
                              )}

                              {/* DEFAULT TEXT */}
                              {!item.type && <p>{item.value}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Approve Claim Modal */}
      <div
        ref={approveModalRef}
        className="modal fade"
        id="approveClaimModal"
        tabIndex="-1"
        aria-labelledby="approveClaimModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="approveClaimModalLabel">
                Approve Claim
              </h5>
            </div>

            <div className="modal-body">
              <h3>Are you sure you want to approve this plate claim?</h3>
              <label className="form-label">
                This will approve the claim and move it to the approved plates
                list.
              </label>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={resolvePlateMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="green-btn"
                onClick={handleApproveClaim}
                disabled={resolvePlateMutation.isPending}
              >
                {resolvePlateMutation.isPending
                  ? "Approving..."
                  : "Approve Claim"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reject Claim Modal */}
      <div
        ref={rejectModalRef}
        className="modal fade"
        id="rejectClaimModal"
        tabIndex="-1"
        aria-labelledby="rejectClaimModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="rejectClaimModalLabel">
                Reject Claim
              </h5>
            </div>

            <div className="modal-body">
              <label className="form-label">
                Please select or enter the reason for rejection
              </label>

              {/* Radio buttons - only one selectable */}
              {rejectReasons.map((reason, index) => (
                <div key={index} className="form-check mb-2">
                  <input
                    type="radio"
                    name="rejectReason"
                    id={`rejectReason${index + 1}`}
                    checked={rejectSubject === reason}
                    onChange={() => handleRejectSubjectChange(reason)}
                    required
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`rejectReason${index + 1}`}
                  >
                    {reason}
                  </label>
                </div>
              ))}

              {/* Textarea */}
              <label className="form-label mt-3">Message</label>
              <textarea
                className="form-control"
                rows="5"
                placeholder="Write here"
                value={rejectMessage}
                onChange={(e) => setRejectMessage(e.target.value)}
                required
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={rejectPlateMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="red-btn"
                onClick={handleRejectClaim}
                disabled={rejectPlateMutation.isPending}
              >
                {rejectPlateMutation.isPending
                  ? "Rejecting..."
                  : "Reject Claim"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Request More Information Modal */}
      <div
        ref={requestInfoModalRef}
        className="modal fade"
        id="requestInfoModal"
        tabIndex="-1"
        aria-labelledby="requestInfoModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="requestInfoModalLabel">
                Request More Information
              </h5>
            </div>

            <div className="modal-body">
              <label className="form-label">Message</label>
              <textarea
                className="form-control"
                rows="5"
                placeholder="Please provide additional information about your vehicle registration and ownership documents."
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                required
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={requestMoreInfoMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="submit-btn"
                onClick={handleRequestMoreInfo}
                disabled={requestMoreInfoMutation.isPending}
              >
                {requestMoreInfoMutation.isPending
                  ? "Sending..."
                  : "Request More Information"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default PlatesClaimsReview;
